import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Tooltip,
  useTheme,
  alpha,
  Chip
} from '@mui/material';
import ContactsIcon from '@mui/icons-material/Contacts';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import HospitalSearch from './HospitalSearch';
import EmergencyServicesSearch from './EmergencyServicesSearch';
import api from '../../utils/api';

const EmergencyContacts = ({ data, onUpdate, styles, initialLocation, projectId }) => {
  const [hospitalSearchOpen, setHospitalSearchOpen] = useState(false);
  const [emergencyServiceSearchOpen, setEmergencyServiceSearchOpen] = useState(false);
  const [currentServiceType, setCurrentServiceType] = useState('police');
  const [projectCrew, setProjectCrew] = useState([]);
  const theme = useTheme();

  // Fetch project crew
  useEffect(() => {
    const fetchCrew = async () => {
      if (!projectId) return;
      try {
        const response = await api.get(`/crew/projects/${projectId}/crew`);
        setProjectCrew(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Error fetching crew:', err);
      }
    };
    fetchCrew();
  }, [projectId]);

  // Helper to get crew member display info (name + phone)
  const getCrewContactInfo = (crewMember) => {
    const name = crewMember?.user?.name || crewMember?.name || crewMember?.userName || '';
    const phone = crewMember?.phone || crewMember?.user?.phone || '';
    return phone ? `${name} - ${phone}` : name;
  };

  // Find crew member by role
  const findCrewByRole = useCallback((roles) => {
    if (!Array.isArray(roles)) roles = [roles];
    for (const role of roles) {
      const member = projectCrew.find(c => c.role === role);
      if (member) return member;
    }
    return null;
  }, [projectCrew]);

  // Auto-populate from crew
  const handleSyncFromCrew = () => {
    const updates = { ...data };
    
    // Location Manager
    const locationManager = findCrewByRole('location-manager');
    if (locationManager && !data.locationManager) {
      updates.locationManager = getCrewContactInfo(locationManager);
    }
    
    // Set Medic
    const setMedic = findCrewByRole('set-medic');
    if (setMedic && !data.setMedic) {
      updates.setMedic = getCrewContactInfo(setMedic);
    }
    
    // Security
    const security = findCrewByRole(['security-coordinator', 'security-guard']);
    if (security && !data.security) {
      updates.security = getCrewContactInfo(security);
    }
    
    // 2nd AD (Second Assistant Director)
    const secondAD = findCrewByRole(['second-assistant-director', 'second-second-ad']);
    if (secondAD && !data.secondAD) {
      updates.secondAD = getCrewContactInfo(secondAD);
    }
    
    // Production Manager (UPM)
    const productionManager = findCrewByRole(['unit-production-manager', 'production-supervisor', 'line-producer']);
    if (productionManager && !data.productionManager) {
      updates.productionManager = getCrewContactInfo(productionManager);
    }
    
    // First AD
    const firstAD = findCrewByRole('first-assistant-director');
    if (firstAD && !data.firstAD) {
      updates.firstAD = getCrewContactInfo(firstAD);
    }
    
    onUpdate(updates);
  };
  
  // Consistent text field styling
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontSize: '0.95rem',
      fontWeight: 600
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.default, 0.3)
        : alpha(theme.palette.grey[50], 0.5),
      borderRadius: 1,
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderWidth: 1.5
      },
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.default, 0.4)
          : alpha(theme.palette.grey[50], 0.8),
        '& fieldset': {
          borderColor: theme.palette.primary.main
        }
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.05)
          : alpha(theme.palette.primary.main, 0.02),
        '& fieldset': {
          borderWidth: 2
        }
      }
    }
  };

  const handleUpdateContact = (field, value) => {
    onUpdate({
      ...data,
      [field]: value
    });
  };

  const handleOpenHospitalSearch = () => {
    setHospitalSearchOpen(true);
  };

  const handleCloseHospitalSearch = () => {
    setHospitalSearchOpen(false);
  };

  const handleSelectHospital = (hospital) => {
    // Format the hospital information with Google Maps data
    const hospitalInfo = `${hospital.name} - ${hospital.address}${hospital.phoneNumber ? ` - ${hospital.phoneNumber}` : ''}${hospital.distance ? ` (${hospital.distance} km away)` : ''}`;
    handleUpdateContact('hospital', hospitalInfo);
    setHospitalSearchOpen(false);
  };
  
  const handleOpenEmergencyServiceSearch = (serviceType) => {
    setCurrentServiceType(serviceType);
    setEmergencyServiceSearchOpen(true);
  };

  const handleCloseEmergencyServiceSearch = () => {
    setEmergencyServiceSearchOpen(false);
  };

  const handleSelectEmergencyService = (service, serviceType) => {
    // Format the emergency service information with Google Maps data
    const serviceInfo = `${service.name} - ${service.phoneNumber}${service.address ? ` - ${service.address}` : ''}${service.distance ? ` (${service.distance} km away)` : ''}`;
    handleUpdateContact(serviceType, serviceInfo);
    setEmergencyServiceSearchOpen(false);
  };

  return (
    <Paper 
      elevation={0}
      sx={{
        ...(styles?.errorSection || {}),
        p: 4,
        mb: 4,
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        backgroundColor: theme.palette.background.paper
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4,
        pb: 3,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ContactsIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography 
              variant="h4" 
              component="h2"
              sx={{ 
                fontWeight: 700,
                letterSpacing: '-0.5px',
                fontSize: '1.75rem',
                color: theme.palette.text.primary,
                mb: 0.5
              }}
            >
              Emergency Contacts
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontSize: '0.875rem'
              }}
            >
              Critical contact information for production safety
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip 
            label={`${Object.keys(data).filter(k => ['police','fire','ambulance','hospital'].includes(k) && data[k]).length} Contacts`} 
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              fontWeight: 600,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
            }}
          />
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(40, 40, 45, 0.8)' : '#fff3e0', 
              height: '100%',
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 16px rgba(0,0,0,0.3)'
                : '0 2px 8px rgba(0,0,0,0.06)',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.palette.mode === 'dark' ? '0 8px 16px rgba(0, 0, 0, 0.4)' : '0 8px 16px rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                mb: 2, 
                color: theme.palette.mode === 'dark' ? theme.palette.info.light : '#1976d2',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <LocalPoliceIcon sx={{ mr: 1.5, fontSize: 28 }} />
              Police
            </Typography>
            <TextField
              fullWidth
              placeholder="Emergency police contact"
              value={data.police || ''}
              onChange={(e) => handleUpdateContact('police', e.target.value)}
              margin="normal"
              variant="outlined"
              sx={textFieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Search for local police departments">
                      <IconButton
                        edge="end"
                        onClick={() => handleOpenEmergencyServiceSearch('police')}
                        sx={{ color: theme.palette.info.main }}
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(40, 40, 45, 0.8)' : '#fff3e0', 
              height: '100%',
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 16px rgba(0,0,0,0.3)'
                : '0 2px 8px rgba(0,0,0,0.06)',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.palette.mode === 'dark' ? '0 8px 16px rgba(0, 0, 0, 0.4)' : '0 8px 16px rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                mb: 2, 
                color: theme.palette.mode === 'dark' ? theme.palette.info.light : '#1976d2',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <LocalFireDepartmentIcon sx={{ mr: 1.5, fontSize: 28 }} />
              Fire Department
            </Typography>
            <TextField
              fullWidth
              placeholder="Emergency fire department contact"
              value={data.fire || ''}
              onChange={(e) => handleUpdateContact('fire', e.target.value)}
              margin="normal"
              variant="outlined"
              sx={textFieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Search for local fire departments">
                      <IconButton
                        edge="end"
                        onClick={() => handleOpenEmergencyServiceSearch('fire')}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper 
            elevation={2}
            sx={{ 
              p: 2.5, 
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(40, 40, 45, 0.8)' : '#e8f5e9', 
              height: '100%',
              borderRadius: 1,
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.palette.mode === 'dark' ? '0 8px 16px rgba(0, 0, 0, 0.4)' : '0 8px 16px rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                mb: 2, 
                color: theme.palette.mode === 'dark' ? theme.palette.info.light : '#1976d2',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <LocalHospitalIcon sx={{ mr: 1.5, fontSize: 28 }} />
              Ambulance
            </Typography>
            <TextField
              fullWidth
              placeholder="Emergency ambulance contact"
              value={data.ambulance || ''}
              onChange={(e) => handleUpdateContact('ambulance', e.target.value)}
              margin="normal"
              variant="outlined"
              sx={textFieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Search for local ambulance services">
                      <IconButton
                        edge="end"
                        onClick={() => handleOpenEmergencyServiceSearch('ambulance')}
                        sx={{ color: theme.palette.success.main }}
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              mt: 1,
              borderRadius: 1,
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(30, 144, 255, 0.1)' 
                : 'rgba(144, 202, 249, 0.15)',
              borderLeft: '3px solid',
              borderColor: theme.palette.info.main,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 16px rgba(0,0,0,0.3)'
                : '0 2px 8px rgba(0,0,0,0.06)'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'medium', 
                mb: 2,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <LocalHospitalIcon color="info" sx={{ mr: 1.5 }} />
              Nearest Hospital Information
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label="Nearest Hospital"
                value={data.hospital || ''}
                onChange={(e) => handleUpdateContact('hospital', e.target.value)}
                margin="normal"
                variant="outlined"
                placeholder="Search for a hospital or enter details manually"
                sx={textFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Search for Hospitals">
                        <IconButton 
                          size="small" 
                          onClick={handleOpenHospitalSearch}
                          edge="end"
                          color="primary"
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Sync from Crew button */}
        <Grid item xs={12}>
          <Button
            variant="text"
            size="small"
            startIcon={<SyncIcon sx={{ fontSize: '14px !important' }} />}
            onClick={handleSyncFromCrew}
            disabled={projectCrew.length === 0}
            sx={{ 
              textTransform: 'none', 
              fontSize: '0.75rem',
              py: 0.5,
              px: 1,
              minHeight: 'auto'
            }}
          >
            Sync from Crew {projectCrew.length > 0 ? `(${projectCrew.length})` : ''}
          </Button>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Production Office"
            value={data.productionOffice || ''}
            onChange={(e) => handleUpdateContact('productionOffice', e.target.value)}
            margin="normal"
            size="small"
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Production Manager"
            value={data.productionManager || ''}
            onChange={(e) => handleUpdateContact('productionManager', e.target.value)}
            margin="normal"
            size="small"
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First AD"
            value={data.firstAD || ''}
            onChange={(e) => handleUpdateContact('firstAD', e.target.value)}
            margin="normal"
            size="small"
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="2nd AD"
            value={data.secondAD || ''}
            onChange={(e) => handleUpdateContact('secondAD', e.target.value)}
            margin="normal"
            size="small"
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Location Manager"
            value={data.locationManager || ''}
            onChange={(e) => handleUpdateContact('locationManager', e.target.value)}
            margin="normal"
            size="small"
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Set Medic"
            value={data.setMedic || ''}
            onChange={(e) => handleUpdateContact('setMedic', e.target.value)}
            margin="normal"
            size="small"
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Security"
            value={data.security || ''}
            onChange={(e) => handleUpdateContact('security', e.target.value)}
            margin="normal"
            size="small"
            sx={textFieldSx}
          />
        </Grid>
      </Grid>

      {/* Hospital Search Dialog */}
      <HospitalSearch
        open={hospitalSearchOpen}
        onClose={handleCloseHospitalSearch}
        onSelect={handleSelectHospital}
        initialLocation={initialLocation || ''}
      />
      
      {/* Emergency Services Search Dialog */}
      <EmergencyServicesSearch
        open={emergencyServiceSearchOpen}
        onClose={handleCloseEmergencyServiceSearch}
        onSelect={handleSelectEmergencyService}
        initialLocation={initialLocation || ''}
        serviceType={currentServiceType}
      />
    </Paper>
  );
};

export default EmergencyContacts;
